/**
 * cloudDemoApi.js — Cloud 5.0 API functions for the V2 Cloud Demo Panel page.
 *
 * Contains cloud-specific functions (event types, palcare devices, groups,
 * v4 alarm create/clear, buildCloudAlarmPayload). Reuses shared functions
 * from demoApi.js (residents, areas, event categories, rounds, check-in, simulate).
 */

import { v4 as uuidv4 } from 'uuid';
import { apiFetch } from '../../../services/api.js';
import { getAuthHeaders } from '../../../services/auth';
import { getUTCTimestamp } from '../../../libs.jsx';

const getBaseUrl = () =>
    import.meta.env.VITE_DEVMODE === 'true'
        ? import.meta.env.VITE_DEVAPI
        : import.meta.env.VITE_API;

const getHeaders = () => getAuthHeaders();

// ---------------------------------------------------------------------------
// READ — Cloud-specific Reference Data
// ---------------------------------------------------------------------------

/**
 * Fetch all event types.
 * V1: ApiRequester myfetch → fetchall att_event_types
 */
export function fetchEventTypes(campusId) {
    return apiFetch({
        procedure: 'fetchall',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
        table: 'att_event_types',
    });
}

/**
 * Fetch PalCare devices (servers) for a campus.
 * V1: ApiRequester myfetch → fetchbyparameter core_palcare_devices
 */
export function fetchPalcareDevices(campusId) {
    return apiFetch({
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
        table: 'core_palcare_devices',
    });
}

/**
 * Fetch all groups for a campus (used for group_ids in alarm payload).
 * V1: ApiRequester myfetch → fetchbyparameter core_groups
 */
export function fetchGroups(campusId) {
    return apiFetch({
        procedure: 'fetchbyparameter',
        reqType: 'direct',
        pKey: 'campus_id',
        pVal: campusId,
        table: 'core_groups',
    });
}

// ---------------------------------------------------------------------------
// WRITE — Cloud Alarm Create / Clear (v4 endpoints)
// ---------------------------------------------------------------------------

/**
 * The v4 endpoint every cloud alarm is created and cleared on: VITE_EVENT_E_ENDPOINT.
 * The non-emergency route (VITE_EVENT_NE_ENDPOINT) is not in use, so there is no
 * per-category split. V1 split on isEmergency(eventCatId), whose list missed System
 * Outage (category 19) and sent its clears to /nonemergency/clear.
 */
function getV4Endpoint() {
    return import.meta.env.VITE_EVENT_E_ENDPOINT;
}

/**
 * Create alarm via the v4emergency POST /create endpoint (all categories).
 * V1: ApiRequester({ reqEndpoint: isEmergency ? "v4emergency" : "v4nonemergency", Method: "POST", Action: "create" })
 * @returns {Promise<[boolean, string?]>}
 */
export async function createCloudAlarm(payload) {
    try {
        const resp = await fetch(getBaseUrl() + getV4Endpoint() + '/create', {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(payload),
        });
        const data = await resp.json();
        return data?.Status === 'OK' ? [true] : [false, 'Failed to create alarm'];
    } catch (err) {
        console.error('[cloudDemoApi] createCloudAlarm error:', err);
        return [false, 'Request failed'];
    }
}

/**
 * Clear alarm via the v4emergency POST /clear endpoint (all categories).
 * V1: ApiRequester({ reqEndpoint: isEmergency ? "v4emergency" : "v4nonemergency", Method: "POST", Action: "clear" })
 * @returns {Promise<[boolean, string?]>}
 */
export async function clearCloudAlarm(payload) {
    try {
        const resp = await fetch(getBaseUrl() + getV4Endpoint() + '/clear', {
            method: 'POST',
            headers: await getHeaders(),
            body: JSON.stringify(payload),
        });
        const data = await resp.json();
        return data?.Status === 'OK' ? [true] : [false, 'Failed to clear alarm'];
    } catch (err) {
        console.error('[cloudDemoApi] clearCloudAlarm error:', err);
        return [false, 'Request failed'];
    }
}

// ---------------------------------------------------------------------------
// PAYLOAD BUILDERS
// ---------------------------------------------------------------------------

/**
 * Build a V4 cloud alarm payload (matching V1 getV4 function).
 * Full 5.0 schema with ~50 fields.
 * @returns {object} — alarm payload for createCloudAlarm
 */
export function buildCloudAlarmPayload({
    dataType, resident, area, device, serverId,
    eventCatId, eventCatName, eventTypeId, eventTypeName,
    session, campusId, residentData, groupList,
}) {
    const timestamp = getUTCTimestamp();
    let residents, areaId, areaName;

    if (dataType === 'resident') {
        residents = [{
            resident_id: resident.resident_id,
            resident_first_name: resident.resident_first_name,
            resident_last_name: resident.resident_last_name,
            resident_name: `${resident.resident_first_name} ${resident.resident_last_name}`,
        }];
        areaId = resident.area_id;
        areaName = resident.area_name;
    } else {
        areaId = area.area_id;
        areaName = area.area_name;
        residents = [];
        (residentData || []).forEach((r) => {
            if (r.area_id === area.area_id) {
                residents.push({
                    resident_id: r.resident_id,
                    resident_first_name: r.resident_first_name,
                    resident_last_name: r.resident_last_name,
                    resident_name: `${r.resident_first_name} ${r.resident_last_name}`,
                });
            }
        });
    }

    const campusConfig = session?.campusData?.[campusId.toString()];

    return {
        campus_id: campusId,
        event_id: uuidv4(),
        device_id: device?.device_id || 0,
        device_name: device?.device_name || '',
        device_description: device?.descriptions || '',
        device_use_id: device?.device_use_id || 0,
        device_type_id: device?.device_type_id || 0,
        manufacture_uuid: device?.manufacture_uuid || '',
        event_category_id: eventCatId,
        event_category: eventCatName,
        event_datetime_utc: timestamp,
        event_stage_id: 1,
        event_stage: 'CREATE',
        event_type_id: eventTypeId,
        event_type: eventTypeName,
        palcare_device_id: serverId,
        residents,
        area_id: areaId,
        area_name: areaName,
        group_ids: groupList || [],
        source: 'Portal Demo Panel',
        generated_by_id: 6,
        timezone_id: campusConfig?.timezone_id,
        timezone_offset: campusConfig?.tz_offset,
        version: '4.0',
        area_group_id: 0,
        area_group_name: '',
        resident_first_name: residents.length > 0 ? residents[0].resident_first_name : '',
        resident_last_name: residents.length > 0 ? residents[0].resident_last_name : '',
        resident_name: residents.length > 0 ? residents[0].resident_name : '',
        resident_id: residents.length > 0 ? residents[0].resident_id : 0,
        manufacture_id: 1,
        manufacture_name: 'Inovonics',
        finish_by_id: 0,
        near_area_id: 0,
        near_area_name: '',
        presses: 1,
        vendor: '',
        vendor_id: 0,
        accepted_display: '',
        accepted_username: '',
        accepted_user_id: 0,
        arrived_datetime_utc: '',
        associated_event_id: '',
        finish_datetime_utc: '',
        ack_datetime_utc: '',
        reason_id: 0,
        notes: '',
        release_datetime_utc: '',
    };
}

/**
 * Build a clear-alarm payload for cloud v4 endpoints.
 * V1: { campus_id, event_id, ack_datetime_utc, event_stage_id: 2, event_stage: "DONE", finish_by_id: 5 }
 */
export function buildClearPayload(campusId, eventId, mId) {
    return {
        campus_id: campusId,
        event_id: eventId,
        manufacture_uuid: mId,
        ack_datetime_utc: getUTCTimestamp(),
        event_stage_id: 2,
        event_stage: 'DONE',
        finish_by_id: 5,
    };
}
